// v3.6r — Bulk MYOB Income Account importer for Mix Designs.
// Accounts staff picks a mode:
//   1. "Same GL for every mix" — one code (e.g. 4-2450) stamped on all mixes.
//   2. "Per-mix" — paste a Code<TAB>GL Code list and fuzzy-match to each mix.
// Preview → Confirm, mirroring the Bulk Card IDs flow.
import { useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertTriangle, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function BulkMixGlCodesDialog({ open, onOpenChange, onDone }) {
  const [mode, setMode] = useState("blanket"); // 'blanket' | 'per_mix'
  const [applyAll, setApplyAll] = useState("4-2450");
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(false);

  const parsed = useMemo(() => {
    if (mode !== "per_mix" || !raw.trim()) return [];
    const rows = [];
    for (const line of raw.split(/\r?\n/)) {
      const t = line.trim();
      if (!t || t.startsWith("#")) continue;
      let parts = t.split("\t");
      if (parts.length < 2) parts = t.split(",");
      if (parts.length < 2) parts = t.split(/\s{2,}/);
      if (parts.length < 2) continue;
      const code = parts[0].trim();
      const gl_code = (parts[1] || "").trim();
      if (code && gl_code) rows.push({ code, gl_code });
    }
    return rows;
  }, [raw, mode]);

  const runPreview = async () => {
    if (mode === "blanket" && !applyAll.trim()) {
      toast.error("Enter a GL code to stamp on every mix");
      return;
    }
    if (mode === "per_mix" && parsed.length === 0) {
      toast.error("No valid rows found. Use one per line: Mix Code<TAB>GL Code");
      return;
    }
    setBusy(true);
    setCommitted(false);
    try {
      const body = mode === "blanket"
        ? { apply_to_all: applyAll.trim(), commit: false }
        : { mappings: parsed, commit: false };
      const { data } = await api.post("/mixes/bulk-gl-codes", body);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview || preview.counts.matched === 0) return;
    setBusy(true);
    try {
      const body = mode === "blanket"
        ? { apply_to_all: applyAll.trim(), commit: true }
        : { mappings: parsed, commit: true };
      const { data } = await api.post("/mixes/bulk-gl-codes", body);
      setPreview(data);
      setCommitted(true);
      toast.success(`Applied ${data.counts.updated} GL code${data.counts.updated === 1 ? "" : "s"}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setRaw("");
    setPreview(null);
    setCommitted(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-none max-w-3xl bg-surface border border-industrial p-0" data-testid="bulk-mix-gl-dialog">
        <DialogHeader className="p-5 border-b border-industrial">
          <DialogTitle className="heading uppercase text-xl font-black flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-orange" />
            Bulk Mix GL Codes · MYOB Income Accounts
          </DialogTitle>
          <div className="text-xs text-muted-industrial mt-1">
            Stamp a MYOB income account (e.g. <span className="mono text-primary-orange">4-2450 · Sales – Concrete</span>) onto your mix designs in one hit. Every batched mix on an invoice line will carry this GL code into the MYOB Professional Sales export.
          </div>
        </DialogHeader>

        <div className="p-5 space-y-4">
          {!preview && (
            <>
              <div className="flex items-center gap-1.5 border border-industrial p-1 w-fit bg-surface-2">
                <button
                  type="button"
                  data-testid="bulk-mix-gl-mode-blanket"
                  onClick={() => setMode("blanket")}
                  className={`px-3 py-1.5 mono text-[11px] uppercase tracking-widest ${
                    mode === "blanket" ? "bg-primary-orange text-black font-bold" : "text-muted-industrial hover:text-primary-orange"
                  }`}
                >Same GL for every mix</button>
                <button
                  type="button"
                  data-testid="bulk-mix-gl-mode-per-mix"
                  onClick={() => setMode("per_mix")}
                  className={`px-3 py-1.5 mono text-[11px] uppercase tracking-widest ${
                    mode === "per_mix" ? "bg-primary-orange text-black font-bold" : "text-muted-industrial hover:text-primary-orange"
                  }`}
                >Per mix (paste list)</button>
              </div>

              {mode === "blanket" && (
                <div>
                  <div className="label-tag mb-2">MYOB Income Account</div>
                  <Input
                    data-testid="bulk-mix-gl-blanket-input"
                    value={applyAll}
                    onChange={(e) => setApplyAll(e.target.value)}
                    placeholder="e.g. 4-2450"
                    className="rounded-none bg-transparent border-industrial mono text-lg h-11 max-w-xs"
                  />
                  <div className="text-[11px] text-muted-industrial mt-2 mono">
                    Every existing mix (and any created later that still has a blank GL) will use this code on its invoice lines.
                  </div>
                </div>
              )}

              {mode === "per_mix" && (
                <div>
                  <div className="label-tag mb-2">Paste Mix Code · GL Code list</div>
                  <textarea
                    data-testid="bulk-mix-gl-textarea"
                    value={raw}
                    onChange={(e) => setRaw(e.target.value)}
                    rows={12}
                    placeholder={"N20\t4-2450\nN25\t4-2450\nN32\t4-2451\nB20\t4-2450\n# Comments and blank lines ignored\n"}
                    className="w-full bg-surface-2 border border-industrial rounded-none px-3 py-2 mono text-xs text-ink focus:outline-none focus:border-primary-orange"
                    spellCheck={false}
                  />
                  <div className="text-[11px] text-muted-industrial mt-1.5 mono">
                    {parsed.length} row{parsed.length === 1 ? "" : "s"} detected · Tab, comma, or 2+ spaces work as separators
                  </div>
                </div>
              )}

              <div className="text-[11px] text-muted-industrial bg-surface-2 border border-industrial p-3">
                <div className="label-tag mb-1">Tip · Finding MYOB income accounts</div>
                In MYOB AccountRight → <b>Accounts → Accounts List</b> → filter to <b>Income</b>. The account number (e.g. <span className="mono">4-2450</span>) is what goes here.
              </div>
            </>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <StatCard label="Input" value={preview.counts.input} tone="muted" />
                <StatCard label="Matched" value={preview.counts.matched} tone="ok" icon={CheckCircle2} />
                <StatCard label="Conflicts" value={preview.counts.conflicts} tone="warn" icon={AlertTriangle} />
                <StatCard label="Unmatched" value={preview.counts.unmatched} tone="err" icon={XCircle} />
              </div>

              {preview.matched.length > 0 && (
                <ResultSection title="Will Update" tone="ok" testId="bulk-mix-gl-matched" rows={
                  preview.matched.map((m) => (
                    <tr key={m.mix_id} className="border-b border-industrial hover:bg-surface-2">
                      <td className="p-2 text-xs">{m.name}</td>
                      <td className="p-2 text-xs mono">→ {m.mix_code} · {m.mix_name}</td>
                      <td className="p-2 text-xs mono text-primary-orange">{m.gl_code}</td>
                      <td className="p-2 text-[10px] text-muted-industrial mono uppercase text-right">{m.kind} · {(m.score * 100).toFixed(0)}%</td>
                    </tr>
                  ))
                } />
              )}

              {preview.conflicts.length > 0 && (
                <ResultSection title="Ambiguous · Skipped" tone="warn" testId="bulk-mix-gl-conflicts" rows={
                  preview.conflicts.map((c, i) => (
                    <tr key={i} className="border-b border-industrial">
                      <td className="p-2 text-xs">{c.name}</td>
                      <td className="p-2 text-[11px] text-muted-industrial" colSpan={3}>
                        Matches: {c.candidates.map((k) => `${k.name} (${(k.score * 100).toFixed(0)}%)`).join(" · ")}
                      </td>
                    </tr>
                  ))
                } />
              )}

              {preview.unmatched.length > 0 && (
                <ResultSection title="No Match · Skipped" tone="err" testId="bulk-mix-gl-unmatched" rows={
                  preview.unmatched.map((u, i) => (
                    <tr key={i} className="border-b border-industrial">
                      <td className="p-2 text-xs">{u.name}</td>
                      <td className="p-2 text-xs mono text-muted-industrial">{u.gl_code}</td>
                      <td className="p-2 text-[11px] text-muted-industrial" colSpan={2}>no mix close enough</td>
                    </tr>
                  ))
                } />
              )}
            </div>
          )}
        </div>

        <div className="border-t border-industrial p-4 flex items-center justify-end gap-2 bg-surface-2">
          {preview ? (
            <>
              <Button variant="ghost" onClick={reset} data-testid="bulk-mix-gl-back" className="rounded-none mono uppercase text-xs tracking-widest">
                Start Over
              </Button>
              {!committed && (
                <Button
                  onClick={commit}
                  disabled={busy || preview.counts.matched === 0}
                  data-testid="bulk-mix-gl-commit"
                  className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black"
                >
                  {busy ? "Applying…" : `Apply to ${preview.counts.matched} mix${preview.counts.matched === 1 ? "" : "es"}`}
                </Button>
              )}
              {committed && (
                <Button
                  onClick={() => onOpenChange(false)}
                  data-testid="bulk-mix-gl-close"
                  className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black"
                >
                  Done
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="bulk-mix-gl-cancel" className="rounded-none mono uppercase text-xs tracking-widest">
                Cancel
              </Button>
              <Button
                onClick={runPreview}
                disabled={busy || (mode === "per_mix" && parsed.length === 0) || (mode === "blanket" && !applyAll.trim())}
                data-testid="bulk-mix-gl-preview"
                className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black"
              >
                {busy
                  ? "Matching…"
                  : mode === "blanket"
                    ? "Preview blanket apply"
                    : `Preview ${parsed.length || 0} Match${parsed.length === 1 ? "" : "es"}`}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, tone, icon: Icon }) {
  const toneStyles = {
    muted: "border-industrial text-muted-industrial",
    ok: "border-emerald-600/40 text-emerald-500",
    warn: "border-amber-500/40 text-amber-500",
    err: "border-rose-600/40 text-rose-500",
  }[tone] || "border-industrial";
  return (
    <div className={`border ${toneStyles} p-3 bg-surface-2`}>
      <div className="flex items-center gap-1.5 label-tag">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="mono text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function ResultSection({ title, tone, rows, testId }) {
  const bar = { ok: "bg-emerald-500", warn: "bg-amber-500", err: "bg-rose-500" }[tone] || "bg-industrial";
  return (
    <div className="border border-industrial" data-testid={testId}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-industrial bg-surface-2">
        <div className={`w-1.5 h-4 ${bar}`} />
        <div className="label-tag">{title}</div>
        <div className="text-[10px] mono text-muted-industrial ml-auto">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
      </div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full">
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}
